import type { SelectItem } from '@/components/Atoms/FaceliftSelect/FaceliftSelect.types';
import { getBvStatuses } from '@/components/Templates/BenefitVerification/BenefitVerificationTable/bv';
import type { BVRecord } from '@/types/benefitsVerification';
import { extractDocumentName, formatDate } from '@/utils';

/**
 * Transforms a BVRecord to the format expected by the Preview component
 * @param record - The BVRecord to transform
 * @param accountProducts - Array of account products for product selection mapping
 * @returns Transformed data object for Preview component
 */
export const transformBVRecordToPreview = (
	record: BVRecord | null,
	accountProducts: Array<{
		label: string;
		value: number;
		qCode: string;
	}> = [],
) => {
	const statuses = getBvStatuses();

	const calculateTotalWounds = () => {
		if (
			record?.woundThreeApplicationCode1 ||
			record?.woundThreeApplicationCode2 ||
			record?.woundThreeDiagnosisCode1 ||
			record?.woundThreeDiagnosisCode2 ||
			record?.woundThreeDiagnosisCode3 ||
			record?.woundThreeDiagnosisCode4 ||
			record?.ta_wound3_woundlength ||
			record?.ta_wound3_woundwidth ||
			record?.ta_wound3_wounddepth ||
			record?.ta_wound3_woundsizetotal ||
			record?.ta_woundtype3
		) {
			return 3;
		}
		if (
			record?.woundTwoApplicationCode1 ||
			record?.woundTwoApplicationCode2 ||
			record?.woundTwoDiagnosisCode1 ||
			record?.woundTwoDiagnosisCode2 ||
			record?.woundTwoDiagnosisCode3 ||
			record?.woundTwoDiagnosisCode4 ||
			record?.ta_wound2_woundlength ||
			record?.ta_wound2_woundwidth ||
			record?.ta_wound2_wounddepth ||
			record?.ta_wound2_woundsizetotal ||
			record?.ta_woundtype2
		) {
			return 2;
		}
		return 1;
	};

	const productsSelected = record?.ta_productselection && accountProducts?.length
				? accountProducts
						.map((item) => item.label)
						.join(', ')
				: record?.ta_productselection || '';
	const productStatuses = 
		record?.priorAuths && accountProducts?.length
			? accountProducts
					.map((product) => {
						const matchingAuth = record.priorAuths?.find(
							(auth) => auth.ta_name === product.label
						);
						if (!matchingAuth) return null;

						const statusKey = String(matchingAuth.ta_pa_status) as keyof typeof statuses;
						return `${product.label}: ${statuses[statusKey] ?? ''}`;
					})
					.filter(Boolean)
					.join(', ')
			: '';
	return {
		clinicName: record?.account?.name || '',
		clinic: record?.account?.accountid || '',
		providerName: record?.provider?.fullname || '',
		patientName: record?.patient?.fullname || '',
		birthdate: record?.ta_dob || '',
		primaryInsurance: record?.primaryInsurance?.ta_name || '',
		secondaryInsurance: record?.secondaryInsurance?.ta_name || '',
		primaryInsuranceMemberID: record?.ta_primarymemberid || '',
		secondaryInsuranceMemberID: record?.ta_secondarymemberid || '',
		emrId: record?.ta_emrid || '',
		isPatientInNursingHome: record?.ta_nursinghome ? 'Yes' : 'No',
		hasPatientHad100DaysOfCare: record?.ta_100days ? 'Yes' : 'No',
		createdOn: record?.createdon ? formatDate(record?.createdon) : '-',
		productSelection: productsSelected,
		productStatuses: productStatuses,
		procedureDate: record?.ta_proceduredate || '',
		noteshartreview: record?.ta_noteshartreview || '',
		wounds: [
			{
				applicationCode1: record?.woundOneApplicationCode1?.ta_name || '',
				applicationCode2: record?.woundOneApplicationCode2?.ta_name || '',
				woundDepth: record?.ta_wounddepth || 0,
				woundLength: record?.ta_woundlength || 0,
				woundWidth: record?.ta_woundwidth || 0,
				woundType: record?.ta_woundtype1?.toString() || '',
				dCode1: record?.woundOneDiagnosisCode1?.ta_name || '',
				dCode2: record?.woundOneDiagnosisCode2?.ta_name || '',
				dCode3: record?.woundOneDiagnosisCode3?.ta_name || '',
				dCode4: record?.woundOneDiagnosisCode4?.ta_name || '',
			},
			{
				applicationCode1: record?.woundTwoApplicationCode1?.ta_name || '',
				applicationCode2: record?.woundTwoApplicationCode2?.ta_name || '',
				woundDepth: record?.ta_wound2_wounddepth || 0,
				woundLength: record?.ta_wound2_woundlength || 0,
				woundWidth: record?.ta_wound2_woundwidth || 0,
				woundType: record?.ta_woundtype2?.toString() || '',
				dCode1: record?.woundTwoDiagnosisCode1?.ta_name || '',
				dCode2: record?.woundTwoDiagnosisCode2?.ta_name || '',
				dCode3: record?.woundTwoDiagnosisCode3?.ta_name || '',
				dCode4: record?.woundTwoDiagnosisCode4?.ta_name || '',
			},
			{
				applicationCode1: record?.woundThreeApplicationCode1?.ta_name || '',
				applicationCode2: record?.woundThreeApplicationCode2?.ta_name || '',
				woundDepth: record?.ta_wound3_wounddepth || 0,
				woundLength: record?.ta_wound3_woundlength || 0,
				woundWidth: record?.ta_wound3_woundwidth || 0,
				woundType: record?.ta_woundtype3?.toString() || '',
				dCode1: record?.woundThreeDiagnosisCode1?.ta_name || '',
				dCode2: record?.woundThreeDiagnosisCode2?.ta_name || '',
				dCode3: record?.woundThreeDiagnosisCode3?.ta_name || '',
				dCode4: record?.woundThreeDiagnosisCode4?.ta_name || '',
			},
		],
		totalWoundsCreated: calculateTotalWounds(),
		portalBVUploadUrl: record?.ta_portalbvupload || '',
		uploadedFiles: record?.ta_portalbvupload
			? [extractDocumentName(record.ta_portalbvupload)]
			: [],
	};
};

/**
 * Core logic for creating enhanced select items with "Add New Code" option
 * @param items - Array of select items
 * @param searchText - Current search text
 * @param selectedValue - Currently selected value
 * @param activeField - Currently active field
 * @param currentField - The field being processed
 * @param hasExactMatch - Whether there's an exact match in the results
 * @param selectedMatchesSearch - Whether the selected value matches the search text
 * @param loading - Whether items are loading
 * @param isCreating - Whether an item is being created
 * @param isValidSearchLength - Function to validate if the search text length is valid
 * @param allowNumericOnly - If true, only numeric input is allowed; if false, alphanumeric is allowed
 * @returns Enhanced array of select items with "Add New Code" option if conditions are met
 */
export const createEnhancedItemsCore = (
	items: SelectItem[],
	searchText: string,
	selectedValue: SelectItem | undefined,
	activeField: string | null,
	currentField: string,
	hasExactMatch: boolean,
	selectedMatchesSearch: boolean,
	loading: boolean,
	isCreating: boolean,
	isValidSearchLength: (searchLength: number) => boolean,
	allowNumericOnly: boolean,
): SelectItem[] => {
	const allItems = [...items];
	const search = searchText || '';
 
	const selectedNotInList = selectedValue && !allItems.some(
		(item) => item.value === selectedValue.value
	);
 
	let resultItems = allItems;
	if (selectedNotInList) {
		resultItems = [selectedValue, ...allItems];
	}
 
	// Validate input format: numeric-only or alphanumeric
	const isValidInputFormat = allowNumericOnly
		? /^\d+$/.test(search.trim()) // Only digits allowed
		: /^[a-zA-Z0-9]+$/.test(search.trim()); // Alphanumeric allowed
 
	if (
		activeField === currentField &&
		isValidSearchLength(search.trim().length) &&
		isValidInputFormat &&
		!hasExactMatch &&
		!selectedMatchesSearch &&
		!loading &&
		!isCreating
	) {
		const addOption: SelectItem = {
			value: `__add__${search}`,
			label: `Add New Code ( ${search} )`,
			__isAddOption: true,
		};
		return [addOption, ...resultItems];
	}
	return resultItems;
};

